import { memo } from 'react';

import type { CroppingAttributes } from './types';
import { getBackgroundPositioning, getCropBox, getZoomBox } from './utils';

export const AUTOSIZE_FIT_NONE = 'none';
export const AUTOSIZE_FIT_DETECT = 'detect';
export const AUTOSIZE_FIT_HORIZONTAL = 'horizontal';
export const AUTOSIZE_FIT_VERTICAL = 'vertical';

type Props = {
  'aria-label'?: string | null;
  autosizeFit?: string | null;
  className?: string;
  height?: number;
  innerRef?: React.RefObject<any>;
  onClick?: React.MouseEventHandler;
  naturalHeight?: number;
  naturalWidth?: number;
  src: string;
  style?: React.CSSProperties;
  width?: number;
} & CroppingAttributes &
  React.HTMLAttributes<HTMLDivElement>;

/**
 * Focused Image
 * =============
 *
 * This component crops an image either vertically or horizontally so that it can be displayed in
 * a container with a different aspect ratio.
 *
 * If the natural image dimensions are not given, this component will attempt to load the image to
 * determine them automatically.
 */

const FocusedImage = (props: Props) => {
  const {
    autosizeFit,
    className,
    height,
    innerRef,
    interestLeft = 0,
    interestTop = 0,
    interestRight = 1,
    interestBottom = 1,
    interestZoom = false,
    naturalWidth,
    naturalHeight,
    src,
    style,
    width,
    ...restProps
  } = props;

  const inlineStyles = {
    ...style,
  };
  if (src) {
    if (inlineStyles.background) {
      inlineStyles.background = `${inlineStyles.background}, url(${src})`;
    } else {
      inlineStyles.backgroundImage = `url(${src})`;
    }
  }

  const interestShiftableX = 1 + (interestLeft - interestRight);
  const interestShiftableY = 1 + (interestTop - interestBottom);

  const interestPositionX = interestShiftableX
    ? `${100 * (interestLeft / interestShiftableX)}%`
    : '50%';
  const interestPositionY = interestShiftableY
    ? `${100 * (interestTop / interestShiftableY)}%`
    : '50%';

  if (naturalWidth && naturalHeight) {
    const displayAspectRatio = width && height ? width / height : undefined;

    const { targetLeft, targetTop, targetWidth, targetHeight } = interestZoom
      ? getZoomBox(
          naturalWidth,
          naturalHeight,
          interestLeft,
          interestTop,
          interestRight,
          interestBottom,
          displayAspectRatio
        )
      : getCropBox(
          naturalWidth,
          naturalHeight,
          interestLeft,
          interestTop,
          interestRight,
          interestBottom,
          displayAspectRatio
        );

    const { positionX, positionY, sizeWidth, sizeHeight } = getBackgroundPositioning(
      naturalWidth,
      naturalHeight,
      targetLeft,
      targetTop,
      targetWidth,
      targetHeight
    );

    const backgroundPosition = `${positionX} ${positionY}`;
    const backgroundSize = `${sizeWidth} ${sizeHeight}`;

    let backgroundPositionAuto = backgroundPosition;
    let backgroundSizeAuto = backgroundSize;
    switch (autosizeFit) {
      case AUTOSIZE_FIT_VERTICAL:
        backgroundPositionAuto = `${interestPositionX} ${positionY}`;
        backgroundSizeAuto = `auto ${sizeHeight}`;
        break;
      case AUTOSIZE_FIT_HORIZONTAL:
        backgroundPositionAuto = `${positionX} ${interestPositionY}`;
        backgroundSizeAuto = `${sizeWidth} auto`;
        break;
      case AUTOSIZE_FIT_NONE:
        break;
      case AUTOSIZE_FIT_DETECT:
      default:
        if (displayAspectRatio != null) {
          if (displayAspectRatio < 1) {
            backgroundPositionAuto = `${interestPositionX} ${positionY}`;
            backgroundSizeAuto = `auto ${sizeHeight}`;
          } else {
            backgroundPositionAuto = `${positionX} ${interestPositionY}`;
            backgroundSizeAuto = `${sizeWidth} auto`;
          }
        }
        break;
    }

    inlineStyles.backgroundPosition = backgroundPositionAuto;
    inlineStyles.backgroundSize = backgroundSizeAuto;
  } else {
    inlineStyles.backgroundPosition = `${interestPositionX} ${interestPositionY}`;
    inlineStyles.backgroundSize = 'cover';
  }

  return (
    <div
      {...restProps}
      className={className}
      aria-hidden="true"
      style={inlineStyles}
      ref={innerRef}
    />
  );
};
export default memo(FocusedImage);
